import React from "react";
import styled from "@emotion/styled";
import { css } from "@emotion/react";
import Button from "@ats/src/components/shared/Button";
import SmallButton from "@ats/src/components/shared/SmallButton";
import DropdownMenu from "@ats/src/components/shared/DropdownMenu";
import PromoCodeCenterModal from "@ats/src/components/modals/PromoCodeCenterModal";
import { useModalContext } from "@shared/context/ModalContext";
import { useToastContext } from "@shared/context/ToastContext";
import { useCreateStripeCustomerPortalSession } from "@shared/queryHooks/useBilling";
import { trackEvent } from "@shared/lib/posthog";
import breakpoint from "@shared/styles/breakpoints";

interface ManageBillingActionsProps {
  hasActiveSubscription?: boolean;
  hasCoupon?: boolean;
  stripePromoCode?: string;
  returnUrl?: string;
}

/* Component */
const ManageBillingActions: React.FC<ManageBillingActionsProps> = ({
  hasActiveSubscription = false,
  hasCoupon = false,
  stripePromoCode,
  returnUrl = "/hire/settings/billing",
}) => {
  window.logger("%c[ManageBillingActions] ", "color: #1976D2", { hasActiveSubscription, hasCoupon, stripePromoCode, returnUrl });
  const { openModal, removeModal } = useModalContext();
  const addToast = useToastContext();
  const { mutate: createStripeCustomerPortalSession } = useCreateStripeCustomerPortalSession();

  const handleOnClickUsePromoCode = () => {
    window.logger("%c[ManageBillingActions] handleOnClickUsePromoCode", "color: #1976D2");

    const modal = <PromoCodeCenterModal onCancel={() => removeModal()} />;

    openModal(modal);
  };

  const handleOnClickManageBilling = () => {
    trackEvent("manage_billing_clicked");
    createStripeCustomerPortalSession(
      { returnUrl },
      {
        onSuccess: (data: any) => {
          window.location.href = data.redirectUrl;
        },
        onError: (error: any) => {
          const errorMessage =
            error?.data?.errors?.general?.[0] || "Unable to access billing portal.";
          addToast({ title: errorMessage, kind: "error" });
        },
      },
    );
  };

  return (
    <Styled.SubscripionActions>
      <SmallButton onClick={handleOnClickManageBilling} styleType="secondary">
        Manage billing
      </SmallButton>
      {hasActiveSubscription && (!hasCoupon || !stripePromoCode) && (
        <Styled.SubscriptionMenu>
          <DropdownMenu testidPrefix="billing-actions">
            <Button onClick={handleOnClickUsePromoCode} styleType="text">
              Use promo code
            </Button>
          </DropdownMenu>
        </Styled.SubscriptionMenu>
      )}
    </Styled.SubscripionActions>
  );
};

export default ManageBillingActions;

/* Styled Components */
let Styled: any = {};

Styled.SubscripionActions = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: ManageBillingActions_SubscripionActions;
    display: flex;
    justify-content: space-between;
    width: 100%;

    ${breakpoint.xs} {
      width: auto;
    }
  `;
});

Styled.SubscriptionMenu = styled.div((props) => {
  const t: any = props.theme;
  return css`
    label: ManageBillingActions_SubscriptionMenu;
    display: flex;
    align-items: center;
    ${t.mt(4)};
    width: 2rem;
    height: 2rem;

    ${breakpoint.xs} {
      width: 1.75rem;
      height: 1.75rem;
      ${t.mt(3)};
    }
  `;
});
